// Capability — the unit of authorization in the capability-based RBAC
// model (E-RBAC / S-310, catalog fleshed out in S-312). Gates check
// capabilities, never role names, so granting a new persona access to an
// action is a map edit (eventually a per-tenant store, S-313) rather than
// a change at every call site.
//
// A capability answers "what action," not "on whose data." Resource reach
// (own tenant vs cross-tenant vs platform/L1) is decided by the scope
// argument to requireCapability — see packages/core/src/auth/.
//
// Each capability below names the legacy per-service guard it replaces so
// the migration stays demonstrably behavior-preserving for the three
// current roles.
import { z } from "zod";
export const CapabilitySchema = z.enum([
    // ── Templates (S-311) ────────────────────────────────────────────────
    // Create / upload template drafts.
    "template:author",
    // Apply taxonomy — manual edit or auto-tag.
    "template:tag",
    // Drive lifecycle transitions (certify / flag / deprecate / peer review).
    "template:certify",
    // ── Tenant administration (S-312) ────────────────────────────────────
    // Platform-level tenant administration: provision a tenant, list all
    // tenants, grant the platform_admin role. Platform-only.
    // (was TenantService.requirePlatformAdmin)
    "platform:manage_tenants",
    // Write tenant config: users, preferences, security, branding,
    // notifications. Admin-of-tenant.
    // (was requireTenantAdminOrPlatformAdmin / requireAdminOfTenant)
    "tenant:manage",
    // Read tenant config / membership. Any member of the tenant.
    // (was requireSameTenantOrPlatformAdmin)
    "tenant:read",
    // ── Catalog + scheduling (S-312) ─────────────────────────────────────
    // Classification catalog writes (deal types, guilds, CBAs). The scope
    // decides reach: platform-scope writes (the global catalog) require
    // platform_admin; tenant-scope writes (a tenant's forked catalog, S-320a)
    // require this capability within that tenant — held by tenant_admin and,
    // cross-tenant, platform_admin. The scope check keeps tenant_admin off
    // the platform catalog (`scope.tenantId === null` → denied).
    // (was ClassificationCatalogService.requirePlatformAdmin)
    "classification:manage",
    // Platform re-attestation schedule operations. Platform-only.
    // (was ReAttestationScheduleService.requirePlatformAdmin)
    "reattestation:manage",
    // ── Ingestion + contracts (S-312) ────────────────────────────────────
    // Submit documents into the ingestion pipeline. Any member of the tenant.
    // (was IngestionService.requireTenantMemberOrPlatformAdmin)
    "ingestion:submit",
    // Replay a failed or stalled document back into the pipeline. Admin-of-
    // tenant, scoped to their own tenant's documents (platform_admin →
    // any tenant). (S-671 — was IngestionService's raw platform_admin-only
    // check; a tenant_admin building their own studio's library had no
    // self-service recovery when a doc failed or stalled mid-pipeline.)
    "ingestion:operate",
    // Read/write structured contract data within a tenant. Any member.
    // (was StructuredContractService.requireTenantMemberOrPlatformAdmin)
    "contract:access",
    // ── Review + attestation (S-312) ─────────────────────────────────────
    // Stage-3 human review actions. Admin-of-tenant.
    // (was Stage3ReviewService.requireAdminOfTenant)
    "review:moderate",
    // Read/write attestation records. Admin-of-tenant (platform_admin → L1,
    // tenant_admin → own L2; tenant_user excluded).
    // (was AttestationService.requireScope)
    "attestation:access",
    // Manage a tenant's template-exclusion list. Admin-of-tenant.
    // (was TenantTemplateExclusionService.requireAdminOfTenant)
    "exclusion:manage",
    // Define/assign a tenant's custom roles + capability sets (S-313).
    // Admin-of-tenant — delegated RBAC for tenant personas.
    "rbac:manage",
    // ── Analytics (S-312) ────────────────────────────────────────────────
    // Read a single tenant's analytics. Admin-of-tenant.
    // (was AnalyticsService.assertScope, tenant branch)
    "analytics:view_tenant",
    // Read cross-tenant / platform-wide analytics. Platform-only.
    // (was AnalyticsService.assertScope, platform branch)
    "analytics:view_platform",
    // ── Precedent visibility (S-273) ─────────────────────────────────────
    // See `business_affairs`-tier precedents. Granted to no built-in role yet —
    // S-275 assigns it to a real Business Affairs membership (via custom-role
    // capabilities). tenant_admin sees the BA tier via role, not this cap.
    "precedent:business_affairs",
    // ── Chat agent personas (S-701 / E-PERSONA-ADMIN) ────────────────────
    // Edit a chat-agent persona's live prompt delta / tools / model / limits
    // (PersonaConfigService, agentPersonaOverrides/{kind}). Platform-scoped —
    // agentRegistry.ts has no tenant concept, so editing one persona changes
    // behavior for every tenant. platform_admin only; deliberately NOT granted
    // to tenant_admin (see ROLE_CAPABILITIES below), matching the other
    // platform-only capabilities (platform:manage_tenants, reattestation:manage).
    "agent:manage",
    // ── Marketplace contributor (S-722) ──────────────────────────────────
    // A contributor (no tenant membership) submits a listing for platform
    // moderation. Scoped by contributorId, not tenantId — see
    // requireCapability.ts's ResourceScope union. (S-723 wires the actual
    // submission flow; this capability ships now with no route yet.)
    "marketplace:submit_listing",
    // Edit/withdraw a contributor's OWN already-submitted listings. Same
    // contributorId scoping as above — never reaches another contributor's
    // listing or any tenant/L1 resource.
    "marketplace:manage_own_listings",
    // [S-723] Approve/reject a contributor's PENDING listing — the platform
    // moderation gate. Deliberately a SEPARATE capability from
    // marketplace:manage_own_listings (which a contributor legitimately
    // holds) rather than reusing it: granting this to contributor would let
    // them approve their own submission, defeating the whole point of a
    // moderation queue. Not granted to any role below — platform_admin only,
    // via the blanket [...CAPABILITIES] grant.
    "marketplace:moderate_listings",
]);
export const CAPABILITIES = CapabilitySchema.options;
// Template capabilities share the admin-of-tenant grant; broken out so the
// resolver tests can assert the uniform template matrix separately from the
// tiered catalog above.
export const TEMPLATE_CAPABILITIES = [
    "template:author",
    "template:tag",
    "template:certify",
];
// ROLE_CAPABILITIES — a behavior-preserving snapshot of *today's* access.
// The scope check in requireCapability keeps tenant-scoped roles to their
// own tenant and off platform/L1 resources, exactly as the legacy guards
// did; this map only encodes "which roles may perform this action at all."
// When this becomes runtime-editable (S-313) the static map stays as the
// platform default.
export const ROLE_CAPABILITIES = {
    // platform_admin can perform every action; scope makes it cross-tenant.
    // (Note: this does NOT grant the requireTenantMember customer-data path,
    // which deliberately *excludes* platform_admin and stays a separate gate.)
    platform_admin: [...CAPABILITIES],
    // tenant_admin: authoring + tenant administration + member actions, but
    // not platform-wide actions (provision / reattestation). Holds
    // `classification:manage` for its *own* tenant's forked catalog (S-320a);
    // the scope check denies it on the platform catalog (tenantId === null).
    tenant_admin: [
        "template:author",
        "template:tag",
        "template:certify",
        "tenant:manage",
        "tenant:read",
        "ingestion:submit",
        "ingestion:operate",
        "contract:access",
        "review:moderate",
        "attestation:access",
        "exclusion:manage",
        "rbac:manage",
        "analytics:view_tenant",
        "classification:manage",
    ],
    // tenant_user: member-level read + submit only (the MCP-consumer role).
    tenant_user: ["tenant:read", "ingestion:submit", "contract:access"],
    // contributor (S-722): the two marketplace-submission capabilities only.
    // No tenant/L1 capability at all — requireCapability's scope check keeps
    // a contributor off every tenant and platform/L1 resource regardless of
    // this list, since a contributor's ResourceScope is never {tenantId},
    // but the list is intentionally this narrow anyway (defense in depth).
    contributor: ["marketplace:submit_listing", "marketplace:manage_own_listings"],
};
//# sourceMappingURL=Capability.js.map